import { type SVGAttributes } from 'react';

export type AccentVariant = 'flower' | 'star' | 'sparkle' | 'heart';

export interface CalendarAccentProps extends SVGAttributes<SVGSVGElement> {
  variant?: AccentVariant;
  size?: number;
}

function FlowerSVG(props: SVGAttributes<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.8" />
      <ellipse cx="12" cy="7" rx="2.5" ry="3.5" fill="currentColor" opacity="0.4" />
      <ellipse cx="12" cy="17" rx="2.5" ry="3.5" fill="currentColor" opacity="0.4" />
      <ellipse cx="7" cy="12" rx="3.5" ry="2.5" fill="currentColor" opacity="0.4" />
      <ellipse cx="17" cy="12" rx="3.5" ry="2.5" fill="currentColor" opacity="0.4" />
      <ellipse cx="8.5" cy="8.5" rx="2.5" ry="3" fill="currentColor" opacity="0.3" transform="rotate(-45 8.5 8.5)" />
      <ellipse cx="15.5" cy="15.5" rx="2.5" ry="3" fill="currentColor" opacity="0.3" transform="rotate(-45 15.5 15.5)" />
    </svg>
  );
}

function StarSVG(props: SVGAttributes<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 2L14.09 8.26L20.18 8.63L15.54 12.74L17.09 19.37L12 15.77L6.91 19.37L8.46 12.74L3.82 8.63L9.91 8.26L12 2Z"
        fill="currentColor"
        opacity="0.5"
      />
    </svg>
  );
}

function SparkleSVG(props: SVGAttributes<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 2C12 2 13.5 8.5 12 12C8.5 13.5 2 12 2 12C2 12 8.5 10.5 12 12C13.5 15.5 12 22 12 22C12 22 10.5 15.5 12 12C15.5 10.5 22 12 22 12C22 12 15.5 13.5 12 12Z"
        fill="currentColor"
        opacity="0.5"
      />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.8" />
    </svg>
  );
}

function HeartSVG(props: SVGAttributes<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
        fill="currentColor"
        opacity="0.4"
      />
    </svg>
  );
}

const variants: Record<AccentVariant, (props: SVGAttributes<SVGSVGElement>) => JSX.Element> = {
  flower: FlowerSVG,
  star: StarSVG,
  sparkle: SparkleSVG,
  heart: HeartSVG,
};

export function CalendarAccent({
  variant = 'flower',
  size = 16,
  className = '',
  style,
  ...props
}: CalendarAccentProps) {
  const Component = variants[variant];

  return (
    <Component
      width={size}
      height={size}
      className={['text-primary inline-block pointer-events-none select-none', className].join(' ')}
      style={{ ...style }}
      {...props}
    />
  );
}
